import { AbstractCommand } from './AbstractCommand';
import { VideoEditor } from '../VideoEditor';
import { VideoElement } from '../models/elements/VideoElement';
import { Track } from '../models/Track';
import { TrackManager } from '../lib/TrackManager';
import { TimelineMode } from './TimelineMode';
import { DeleteLayoutElementsCommand } from './DeleteLayoutElementsCommand';
import { TrimLayoutElementsCommand } from './TrimLayoutElementsCommand';
import { ExciseLayoutElementsCommand } from './ExciseLayoutElementsCommand';

/**
 * Possible operations for the simple remove.
 */
type OperationType = 'delete' | 'trimStart' | 'trimEnd' | 'excise';

interface Operation {
  type: OperationType;
  element: VideoElement;
  data?: {
    newStartTime?: number; // for trimStart
    newEndTime?: number; // for trimEnd
    mediaStart?: number; // for excise
    mediaEnd?: number; // for excise
  };
}

/**
 * TranscriptSimpleRemoveCommand – removes a contiguous range of transcript
 * words (identified by their original `initial_index` values) from a single
 * VideoElement.  Based on the range it decides whether to:
 *   • delete the element entirely
 *   • trim the start of the element
 *   • trim the end of the element
 *   • excise (split) a middle section of the element
 *
 * It stores enough state for a complete undo.
 */
export class TranscriptSimpleRemoveCommand extends AbstractCommand {
  private readonly elementId: string;
  private readonly startInitialIdx: number;
  private readonly endInitialIdx: number;
  private readonly trimTail: boolean;
  private readonly trimHead: boolean;

  private element: VideoElement | null = null;
  private transcriptTrack: Track | null = null;
  private readonly trackManager: TrackManager;

  // Undo state
  private deletedElementState: Record<string, any> | null = null;
  private trimmedOriginalState: Record<string, any> | null = null;
  private excisedOriginalState: Record<string, any> | null = null;
  private excisedNewElements: VideoElement[] = [];

  constructor(
    editor: VideoEditor,
    elementId: string,
    startInitialIdx: number,
    endInitialIdx: number,
    trimHead: boolean,
    trimTail: boolean,
  ) {
    super(editor);

    this.trimHead = trimHead;
    this.trimTail = trimTail;
    this.elementId = elementId;
    // Normalise ordering
    this.startInitialIdx = Math.min(startInitialIdx, endInitialIdx);
    this.endInitialIdx = Math.max(startInitialIdx, endInitialIdx);

    this.trackManager = this.editor.getTrackManager();
  }

  // ---------------------------------------------------------------------
  // Command lifecycle
  // ---------------------------------------------------------------------

  execute(): boolean {
    const element = this.lookupElement();
    if (!element) return false;

    const op = this.determineOperation(element);
    if (!op) return false;

    this.applyOperation(op);

    // Recalc timeline and mark dirty so observers fire.
    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_remove');

    return true;
  }

  undo(): boolean {
    const element = this.lookupElement(true); // allow missing

    // Undo in reverse order of precedence: excise -> trim -> delete
    if (this.excisedOriginalState && element) {
      // Remove the two parts if they still exist
      this.excisedNewElements.forEach((el) => {
        this.editor.timeline.removeElement(el.id);
      });

      // Re-add original element to its track if missing
      const track = this.getTranscriptTrack();
      if (track && !track.elements.some((e) => e.id === element.id)) {
        track.addElement(element);
      }

      element.restoreState(this.excisedOriginalState);
    }

    if (this.trimmedOriginalState && element) {
      element.restoreState(this.trimmedOriginalState);
    }

    if (this.deletedElementState) {
      // Restore (or recreate) deleted element
      let el: VideoElement | null = element;

      if (!el) {
        // Recreate from stored state JSON
        el = VideoElement.fromJSON(
          this.deletedElementState as Record<string, any>,
        );
      } else {
        el.restoreState(this.deletedElementState);
      }

      // Ensure it exists on the timeline
      const track = this.getTranscriptTrack();
      if (track && !track.elements.some((e) => e.id === this.elementId)) {
        track.addElement(el);
      }
    }

    this.editor.timeline.recalculateDuration();
    this.editor.markDirty('transcript_simple_remove_undo');

    return true;
  }

  getDescription(): string {
    return `Simple remove words (${this.startInitialIdx}-${this.endInitialIdx}) from element ${this.elementId}`;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private lookupElement(allowMissing = false): VideoElement | null {
    if (this.element) return this.element;
    const el = this.editor.getElementById(this.elementId);
    if (!(el instanceof VideoElement)) {
      // if (!Element.isVideoElement(el)) {
      return allowMissing ? null : null;
    }
    this.element = el;
    return this.element;
  }

  private getTranscriptTrack(): Track | null {
    if (this.transcriptTrack) return this.transcriptTrack;
    const track = this.editor.timeline.getTrackForElement(this.elementId);
    this.transcriptTrack = track ?? null;
    return this.transcriptTrack;
  }

  private determineOperation(element: VideoElement): Operation | null {
    const segment = element.getTranscriptSegment();
    if (!segment) return null;

    const words = segment.getWordsWithTimestamps();
    if (words.length === 0) return null;

    // Words are chronologically ordered; ensure sorted by initial_index to be safe
    const sorted = [...words].sort((a, b) => a.initial_index - b.initial_index);

    const firstWord = sorted[0];
    const lastWord = sorted[sorted.length - 1];

    const rangeHitsStart =
      this.startInitialIdx <= firstWord.initial_index &&
      (this.trimHead || firstWord.relative_ts <= 0);
    const rangeHitsEnd =
      this.endInitialIdx >= lastWord.initial_index &&
      (this.trimTail || lastWord.relative_end_ts >= element.durationSeconds);

    if (rangeHitsStart && rangeHitsEnd) {
      return { type: 'delete', element };
    }

    if (rangeHitsStart) {
      const nextWord = sorted.find((w) => w.initial_index > this.endInitialIdx);
      if (nextWord) {
        const newStartTime = element.getMediaStartTime() + nextWord.relative_ts;
        return { type: 'trimStart', element, data: { newStartTime } };
      }
      return { type: 'delete', element };
    }

    if (rangeHitsEnd) {
      const prevWord = [...sorted]
        .reverse()
        .find((w) => w.initial_index < this.startInitialIdx);
      if (prevWord) {
        const newEndTime = element.time + prevWord.relative_end_ts;
        return { type: 'trimEnd', element, data: { newEndTime } };
      }
      return { type: 'delete', element };
    }

    // Excise middle section
    const firstCut = sorted.find(
      (w) => w.initial_index >= this.startInitialIdx,
    );
    const lastCut = [...sorted]
      .reverse()
      .find((w) => w.initial_index <= this.endInitialIdx);

    if (firstCut && lastCut) {
      return {
        type: 'excise',
        element,
        data: {
          mediaStart: firstCut.relative_ts,
          mediaEnd: lastCut.relative_end_ts,
        },
      };
    }

    return null;
  }

  private applyOperation(op: Operation): void {
    switch (op.type) {
      case 'delete':
        this.applyDeleteOperation(op);
        break;
      case 'trimStart':
        this.applyTrimStartOperation(op);
        break;
      case 'trimEnd':
        this.applyTrimEndOperation(op);
        break;
      case 'excise':
        this.applyExciseOperation(op);
        break;
    }
  }

  private applyDeleteOperation(op: Operation): void {
    if (this.extraElementData.isLayoutElement(op.element)) {
      const command = new DeleteLayoutElementsCommand(this.editor, op.element);
      command.execute();
    } else {
      this.deletedElementState = op.element.getState();
      this.trackManager.removeElement(op.element, TimelineMode.RIPPLE);
    }
  }

  private applyTrimStartOperation(op: Operation): void {
    if (this.extraElementData.isLayoutElement(op.element)) {
      const command = new TrimLayoutElementsCommand(this.editor, op.element, {
        trimType: 'start',
        trimTime: op.data?.newStartTime ?? 0,
      });
      command.execute();
    } else {
      this.trimmedOriginalState = op.element.getState();
      this.trackManager.trimMediaStart(
        op.element,
        op.data?.newStartTime ?? 0,
        TimelineMode.RIPPLE,
        false,
      );
    }
  }

  private applyTrimEndOperation(op: Operation): void {
    if (this.extraElementData.isLayoutElement(op.element)) {
      const command = new TrimLayoutElementsCommand(this.editor, op.element, {
        trimType: 'end',
        trimTime: op.data?.newEndTime ?? 0,
      });
      command.execute();
    } else {
      this.trimmedOriginalState = op.element.getState();
      this.trackManager.trimElementEnd(
        op.element,
        op.data?.newEndTime ?? 0,
        TimelineMode.RIPPLE,
        false,
      );
    }
  }

  private applyExciseOperation(op: Operation): void {
    if (this.extraElementData.isLayoutElement(op.element)) {
      const command = new ExciseLayoutElementsCommand(this.editor, op.element, {
        mediaRemovalStart: op.data?.mediaStart ?? 0,
        mediaRemovalEnd: op.data?.mediaEnd ?? 0,
      });
      command.execute();
    } else {
      this.excisedOriginalState = op.element.getState();
      const res = this.trackManager.exciseTimeRangeFromElement(
        op.element,
        op.data?.mediaStart ?? 0,
        op.data?.mediaEnd ?? 0,
        TimelineMode.RIPPLE,
      );
      if (res) {
        this.excisedNewElements = [
          res.firstPart as VideoElement,
          res.secondPart as VideoElement,
        ];
      }
    }
  }
}
